import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  RefreshCw, TrendingUp, MousePointer, Eye, DollarSign, 
  Target, BarChart3, AlertCircle, ExternalLink, ArrowLeft,
  Play, Pause, CheckCircle
} from 'lucide-react';
import * as api from '../services/api';
import { toast } from 'sonner';

const CampaignsAdwords = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [accountSummary, setAccountSummary] = useState(null);
  const [dateRange, setDateRange] = useState('LAST_30_DAYS');
  const [error, setError] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    checkConfigAndLoad();
  }, []);

  useEffect(() => {
    if (isConfigured) {
      loadCampaigns();
      loadAccountSummary();
    }
  }, [dateRange, isConfigured]);

  const checkConfigAndLoad = async () => {
    try {
      const status = await api.getGoogleAdsStatus();
      setIsConfigured(status.configured);
      if (!status.configured) {
        setError('Google Ads no está configurado. Contacte al administrador.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error checking Google Ads status:', err);
      setError('Error al verificar la configuración de Google Ads');
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getGoogleAdsCampaigns();
      setCampaigns(data.campaigns || []);
    } catch (err) {
      console.error('Error loading campaigns:', err);
      setError(err.response?.data?.detail || 'Error al cargar campañas');
      toast.error('Error al cargar campañas de Google Ads');
    } finally {
      setLoading(false);
    }
  };

  const loadAccountSummary = async () => {
    try {
      const data = await api.getGoogleAdsAccountSummary(null, dateRange);
      setAccountSummary(data);
    } catch (err) {
      console.error('Error loading account summary:', err);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('es-CO').format(value);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ENABLED: { label: 'Activa', color: 'bg-green-100 text-green-800', icon: Play },
      PAUSED: { label: 'Pausada', color: 'bg-yellow-100 text-yellow-800', icon: Pause },
      REMOVED: { label: 'Eliminada', color: 'bg-red-100 text-red-800', icon: AlertCircle }
    };
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800', icon: AlertCircle };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getChannelLabel = (channelType) => {
    const channels = {
      SEARCH: 'Búsqueda',
      DISPLAY: 'Display',
      SHOPPING: 'Shopping',
      VIDEO: 'Video',
      MULTI_CHANNEL: 'Multicanal',
      LOCAL: 'Local',
      SMART: 'Smart',
      PERFORMANCE_MAX: 'Performance Max',
      DISCOVERY: 'Discovery'
    };
    return channels[channelType] || channelType;
  };

  const dateRangeOptions = [
    { value: 'TODAY', label: 'Hoy' },
    { value: 'YESTERDAY', label: 'Ayer' },
    { value: 'LAST_7_DAYS', label: 'Últimos 7 días' },
    { value: 'LAST_14_DAYS', label: 'Últimos 14 días' },
    { value: 'LAST_30_DAYS', label: 'Últimos 30 días' },
    { value: 'THIS_MONTH', label: 'Este mes' },
    { value: 'LAST_MONTH', label: 'Mes anterior' },
    { value: 'LAST_90_DAYS', label: 'Últimos 90 días' }
  ];

  if (error && !isConfigured) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <h1 className="text-2xl font-bold">Google Ads - Campañas</h1>
        </div>
        
        <Card className="max-w-2xl mx-auto mt-12">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <AlertCircle className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Configuración Requerida</h2>
              <p className="text-slate-600 mb-4">{error}</p>
              <p className="text-sm text-slate-500">
                Para habilitar la integración con Google Ads, se necesitan las credenciales de la API.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Google Ads - Campañas</h1>
            <p className="text-sm text-slate-500">
              Rendimiento de tus campañas publicitarias
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-48" data-testid="date-range-select">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              {dateRangeOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            onClick={() => { loadCampaigns(); loadAccountSummary(); }}
            disabled={loading}
            data-testid="refresh-campaigns-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {accountSummary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-blue-100">
                  <Eye className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Impresiones</p>
                  <p className="text-2xl font-bold">{formatNumber(accountSummary.totals.impressions)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-green-100">
                  <MousePointer className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Clics</p>
                  <p className="text-2xl font-bold">{formatNumber(accountSummary.totals.clicks)}</p>
                  <p className="text-xs text-slate-400">CTR: {accountSummary.totals.ctr}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-purple-100">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Costo Total</p>
                  <p className="text-2xl font-bold">{formatCurrency(accountSummary.totals.cost)}</p>
                  <p className="text-xs text-slate-400">CPC: {formatCurrency(accountSummary.totals.avgCpc)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-orange-100">
                  <Target className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Conversiones</p>
                  <p className="text-2xl font-bold">{formatNumber(Math.round(accountSummary.totals.conversions))}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Campaign Counts */}
      {accountSummary?.campaignCounts && (
        <div className="flex gap-4 text-sm">
          <span className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-slate-600">{accountSummary.campaignCounts.ENABLED || 0} Activas</span>
          </span>
          <span className="flex items-center gap-2">
            <Pause className="w-4 h-4 text-yellow-500" />
            <span className="text-slate-600">{accountSummary.campaignCounts.PAUSED || 0} Pausadas</span>
          </span>
          <span className="text-slate-400">|</span>
          <span className="text-slate-600">{accountSummary.campaignCounts.total || 0} Total</span>
        </div>
      )}

      {/* Campaigns Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Listado de Campañas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
              <span className="ml-3 text-slate-500">Cargando campañas...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
              <p className="text-slate-600">{error}</p>
              <Button variant="outline" className="mt-4" onClick={loadCampaigns}>
                Reintentar
              </Button>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay campañas para mostrar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaña</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Canal</TableHead>
                    <TableHead className="text-right">Impresiones</TableHead>
                    <TableHead className="text-right">Clics</TableHead>
                    <TableHead className="text-right">CTR</TableHead>
                    <TableHead className="text-right">Costo</TableHead>
                    <TableHead className="text-right">CPC</TableHead>
                    <TableHead className="text-right">Conversiones</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.id} data-testid={`campaign-row-${campaign.id}`}>
                      <TableCell className="font-medium max-w-xs truncate">
                        {campaign.name}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(campaign.status)}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs bg-slate-100 px-2 py-1 rounded">
                          {getChannelLabel(campaign.channelType)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatNumber(campaign.impressions)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatNumber(campaign.clicks)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {campaign.ctr}%
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(campaign.cost)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(campaign.avgCpc)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {Math.round(campaign.conversions)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/campaigns/adwords/${campaign.id}`)}
                          data-testid={`view-campaign-${campaign.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CampaignsAdwords;
